import React, { useEffect, useState } from "react";
import type { SupportCategory } from "@/v2-components/pages/support/types/support-data";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import { cn } from "@/libs/utils";
import { useRouter } from "next/router";
import { isNodeInPath } from "@/v2-components/pages/support/utils/support-category-node-util";

/*
 support contents section left tree
 */
export const SupportMainContentsTree = ({
  tree,
  nodeClickCb,
  className,
  query = "categorySlug",
}: {
  tree: SupportCategory[];
  nodeClickCb?: () => void;
  className?: string;
  query?: string;
}) => {
  if (!tree.length) return;
  return (
    <ul className={cn("flex flex-col gap-2 pt-8 text-lg font-400", className)}>
      {tree.map((node) => (
        <SupportMainContentsNode
          key={node.id}
          node={node}
          nodeClickCb={nodeClickCb}
          query={query}
        />
      ))}
    </ul>
  );
};

const SupportMainContentsNode = ({
  node,
  deps = 0, //현재 deps
  nodeClickCb,
  query,
}: {
  node: SupportCategory;
  deps?: number;
  nodeClickCb?: () => void;
  query: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const router = useRouter();
  const { categorySlug } = router.query;

  // 현재 노드와 선택된 노드 비교
  const isSelectedNode = node.id === categorySlug;

  // 선택된 노드가 하위 노드 중 하나일 경우 열림 상태 유지
  useEffect(() => {
    if (isSelectedNode || isNodeInPath(node, categorySlug ?? "")) {
      setIsOpen(true);
    }
  }, [categorySlug]);

  const hasChildren = node.children && node.children.length > 0;
  const isRecursion = isOpen && hasChildren;
  const isLastNode = !hasChildren && node.id;

  const navigateToNode = (nodeId: string) => {
    nodeClickCb?.();
    router.push(
      { pathname: router.pathname, query: { [query]: nodeId } },
      undefined,
      { shallow: true, scroll: false },
    );
  };

  const getPaddingLeftClass = (deps: number) => `pl-${deps * 4}`;

  // 마지막 노드인 경우
  if (isLastNode) {
    return (
      <li className={cn("ml-5", getPaddingLeftClass(deps))}>
        <p
          className={cn("cursor-pointer", { "font-600": isSelectedNode })}
          onClick={() => navigateToNode(node.id)}
        >
          {node.label}
        </p>
      </li>
    );
  }

  // 하위 노드가 있는 경우
  return (
    <li>
      <div
        className={cn(
          "flex cursor-pointer items-center gap-3",
          getPaddingLeftClass(deps),
          {
            "font-700": isSelectedNode,
            "font-600": isOpen,
          },
        )}
        onClick={() => setIsOpen(!isOpen)}
      >
        {hasChildren && (
          <span
            className={`flex-shrink-0 transition-transform ${
              isOpen ? "rotate-90" : "rotate-0"
            }`}
          >
            <ChevronRightIcon className="h-5 w-5" />
          </span>
        )}
        <span>{node.label}</span>
      </div>

      {/*재귀*/}
      {isRecursion && (
        <ul className="flex flex-col gap-2 pt-2">
          {node.children?.map((child) => (
            <SupportMainContentsNode
              key={child.id}
              node={child}
              deps={deps + 1}
              nodeClickCb={nodeClickCb}
              query={query}
            />
          ))}
        </ul>
      )}
    </li>
  );
};
